import moment from "moment";
import "moment/locale/es";
import supabase from "../supabase-client";

const getReservationTime = (fromDate, fromHours, toDate, toHours) => {
  if (fromDate === "" || toDate === "") return;
  const from = moment(`${fromDate} ${fromHours}`, "YYYY-MM-DD HH:mm");
  const to = moment(`${toDate} ${toHours}`, "YYYY-MM-DD HH:mm");
  let days = to.diff(from, "days");
  let hours = to.diff(from, "hours") % 24;
  const minutes = (to.diff(from, "minutes") % 1440) - 60 * hours;
  hours = minutes === 30 ? (hours += 1) : hours;
  // 6+ extra hours rounds up to a full extra day
  if (hours > 5) {
    days += 1;
    hours = 0;
  }
  return { days, hours };
};

// Active price lists, ordered by priority (lower number = higher priority, so
// the first row is the default pre-selection everywhere internal).
const getPriceLists = async () => {
  const { data, error } = await supabase
    .from("price_lists")
    .select("id, title, priority, is_active, is_public")
    .eq("is_active", true)
    .order("priority", { ascending: true })
    .order("id", { ascending: true });
  if (error) {
    console.error(error.message);
    return [];
  }
  return data || [];
};

// Default list id for internal contexts: the highest-priority active list.
const getDefaultPriceListId = async () => {
  const lists = await getPriceLists();
  return lists[0]?.id ?? null;
};

const calculateReservationTotal = async (
  { days, hours },
  car_id,
  payment,
  insurance,
  license,
  priceListId = null
) => {
  // 6+ extra hours counts as a full extra day
  var hoursMultiplier = 0;
  if (hours <= 2) {
    hoursMultiplier = 0;
  } else if (hours <= 5) {
    hoursMultiplier = 0.3;
  } else {
    days += 1;
    hours = 0;
  }

  var timeFrame = undefined;

  if (days <= 1) {
    timeFrame = "1";
  } else if (days <= 3) {
    timeFrame = "2-3";
  } else if (days <= 6) {
    timeFrame = "4-6";
  } else if (days <= 14) {
    timeFrame = "7-14";
  } else if (days > 14) {
    timeFrame = "15-30";
  }

  const query = `base_${timeFrame}${
    insurance !== "base" ? `, ${insurance}_${timeFrame}` : ""
  }${payment !== "card" ? `, cash_${timeFrame}` : ""}, deposit_${
    license !== "local" ? "foreign" : insurance
  }`;
  // prices now has one row per (price_list_id, model), so the lookup MUST be
  // scoped to a list. Fall back to the default (top-priority) list.
  let listId = priceListId;
  if (listId == null) listId = await getDefaultPriceListId();
  const { data, error } = await supabase
    .from("prices")
    .select(query)
    .eq("id", car_id)
    .eq("price_list_id", listId)
    .single();
  if (error) {
    console.error(error.message);
    return { success: false, error: error.message };
  }

  var dailyPrice = 0;
  var deposit = 0;

  Object.keys(data).forEach(function (key) {
    if (key.startsWith("deposit")) {
      deposit = data[key];
    } else {
      dailyPrice += data[key];
    }
  });
  if (days === 0) days = 1;
  const hoursExtra = dailyPrice * hoursMultiplier;
  const totalRent = dailyPrice * days + hoursExtra;

  return { dailyPrice, deposit, hoursExtra, totalRent, time: { days, hours } };
};

// Clear billable_amount on a reservation's open price period.
// Must be called BEFORE updating reservations.date_range or restructuring
// sections — compute_reservation_total_rent treats a non-null billable_amount
// as the frozen section total and would otherwise return that stale value
// instead of recomputing days × daily_price for the new range.
const clearOpenSectionBillableAmount = async (reservationId) => {
  const { data: openSection, error: errLookup } = await supabase
    .from("reservation_price_periods")
    .select("id, billable_amount")
    .eq("reservation_id", reservationId)
    .is("effective_to", null)
    .maybeSingle();
  if (errLookup) return { error: errLookup };
  if (!openSection || openSection.billable_amount == null) return { error: null };
  const { error } = await supabase
    .from("reservation_price_periods")
    .update({ billable_amount: null })
    .eq("id", openSection.id);
  return { error };
};

// Manually fire sync_reservation_totals for a reservation. Use after section
// restructuring (close/insert/delete) when no date_range UPDATE was performed
// — the date-range trigger only fires when date_range itself changes.
const resyncReservationTotals = async (reservationId) => {
  return await supabase.rpc("sync_reservation_totals", { p_res_id: reservationId });
};

export {
  getReservationTime,
  calculateReservationTotal,
  clearOpenSectionBillableAmount,
  resyncReservationTotals,
  getPriceLists,
  getDefaultPriceListId,
};
